import { useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { CtaLink, PageIntro, Reveal, SelectField, TextArea, TextField } from '../components/ui';
import { siteInfo } from '../components/layout/siteInfo';
import { useNavData } from '../components/layout/useNavData';

const INQUIRIES = ['Demande de devis', 'Renseignement sur un service', 'Partenariat', 'Suivi de projet', 'Autre demande'];
const emailOk = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
const phoneOk = (v: string) => v.replace(/[^\d]/g, '').length >= 8;

type Errors = Partial<Record<'inquiry' | 'name' | 'phone' | 'email' | 'message', string>>;

export default function Contact() {
  const [params] = useSearchParams();
  const { services } = useNavData();
  const preset = params.get('service') ?? '';

  const [form, setForm] = useState({ inquiry: preset ? 'Demande de devis' : '', service: preset, name: '', phone: '', email: '', message: '', newsletter: false });
  // La liste des services arrive après le premier affichage: l'option présélectionnée doit exister avant d'être choisie
  const [errors, setErrors] = useState<Errors>({});
  const [sent, setSent] = useState(false);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const serviceName = useMemo(() => services.find((s) => s.id === form.service)?.name ?? '', [services, form.service]);

  const validate = (): Errors => {
    const er: Errors = {};
    if (!form.inquiry) er.inquiry = 'Choisissez le type de demande.';
    if (form.name.trim().length < 2) er.name = 'Indiquez votre nom.';
    if (!phoneOk(form.phone)) er.phone = 'Indiquez un numéro valide.';
    if (!emailOk(form.email)) er.email = 'Indiquez une adresse e-mail valide.';
    if (form.message.trim().length < 10) er.message = 'Décrivez votre demande en quelques mots.';
    return er;
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const er = validate();
    setErrors(er);
    if (Object.keys(er).length > 0) return;

    // Aucun point d'accès « contact » côté API: le message est préparé dans la messagerie du visiteur.
    const body = [
      'Bonjour,',
      '',
      form.message.trim(),
      '',
      `Type de demande : ${form.inquiry}`,
      ...(serviceName ? [`Service concerné : ${serviceName}`] : []),
      `Téléphone : ${form.phone}`,
      `E-mail : ${form.email}`,
      '',
      'Cordialement,',
      form.name.trim(),
    ].join('\n');
    const subject = `${form.inquiry}${serviceName ? ` - ${serviceName}` : ''} (site Flex Group)`;
    window.location.href = `mailto:${siteInfo.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    if (form.newsletter) apiService.subscribeNewsletter(form.email).catch(() => {});
    setSent(true);
  };

  const side = [
    { title: 'Nous écrire', text: siteInfo.email, cta: { label: 'Envoyer un e-mail', href: `mailto:${siteInfo.email}` } },
    { title: 'Nous appeler', text: siteInfo.phone, cta: { label: 'Appeler maintenant', href: siteInfo.phoneHref } },
    { title: 'Nous rendre visite à Kinshasa', text: siteInfo.address, cta: { label: 'Voir sur la carte', href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(siteInfo.address)}` } },
    { title: 'Nous rendre visite au Maniema', text: siteInfo.addressManiema, cta: { label: 'Voir sur la carte', href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(siteInfo.addressManiema)}` } },
  ];

  return (
    <div id="content" className="text-black">
      <PageIntro eyebrow="Contact" title="Parlons de votre projet." motif="arcs" />

      <section className="bg-white pb-20 md:pb-28">
        <div className="site-container grid gap-14 lg:grid-cols-[1.45fr_1fr] lg:gap-0">
          {/* Formulaire */}
          <div className="lg:pr-20">
            <Reveal><h2 className="font-display text-[2rem] font-bold normal-case leading-[1.1] tracking-normal text-black md:text-[2.75rem]">Comment pouvons-nous vous aider ?</h2></Reveal>

            {sent ? (
              <div role="status" className="mt-10 border-l-2 border-brand bg-surface p-8">
                <h3 className="font-display text-[1.75rem] font-bold normal-case leading-tight tracking-normal text-black">Votre message est prêt</h3>
                <p className="mt-3 max-w-lg text-[1.0625rem] leading-relaxed">Votre messagerie vient de s'ouvrir avec votre demande: il ne reste qu'à l'envoyer. Si rien ne s'est ouvert, écrivez-nous à <a className="font-semibold text-brand-darker" href={`mailto:${siteInfo.email}`}>{siteInfo.email}</a>.</p>
                <button onClick={() => { setSent(false); setForm({ inquiry: '', service: '', name: '', phone: '', email: '', message: '', newsletter: false }); }} className="mt-6 text-[0.875rem] font-semibold uppercase tracking-[0.094rem] text-charcoal underline decoration-brand underline-offset-4">Écrire un autre message</button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="mt-10 grid gap-x-6 gap-y-6 sm:grid-cols-2">
                <SelectField id="c-inquiry" label="Type de demande" required value={form.inquiry} onChange={set('inquiry')} error={errors.inquiry}>
                  <option value="" disabled>Choisir…</option>
                  {INQUIRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                </SelectField>
                <SelectField id="c-service" label="Service concerné" value={form.service} onChange={set('service')}>
                  <option value="">Non précisé</option>
                  {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  {form.service && !services.some((s) => s.id === form.service) && <option value={form.service}>Chargement…</option>}
                </SelectField>
                <TextField id="c-name" label="Nom complet" required autoComplete="name" value={form.name} onChange={set('name')} error={errors.name} className="sm:col-span-2" />
                <TextField id="c-phone" label="Téléphone" required type="tel" autoComplete="tel" value={form.phone} onChange={set('phone')} error={errors.phone} />
                <TextField id="c-email" label="E-mail" required type="email" autoComplete="email" value={form.email} onChange={set('email')} error={errors.email} />
                <TextArea id="c-message" label="Description" required value={form.message} onChange={set('message')} error={errors.message} placeholder="Décrivez votre projet ou votre demande" className="sm:col-span-2" />

                <label className="flex cursor-pointer items-center gap-3 text-[0.9375rem] sm:col-span-2">
                  <input type="checkbox" className="peer sr-only" checked={form.newsletter} onChange={(e) => setForm((f) => ({ ...f, newsletter: e.target.checked }))} />
                  <span aria-hidden className="flex h-5 w-5 shrink-0 items-center justify-center border border-charcoal bg-white transition-colors peer-checked:border-brand-dark peer-checked:bg-brand-dark peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-charcoal">
                    <svg viewBox="0 0 12 10" className={`h-3 w-3 text-white ${form.newsletter ? 'opacity-100' : 'opacity-0'}`} fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 5l3.5 3.5L11 1.5" /></svg>
                  </span>
                  Je souhaite recevoir vos actualités par e-mail
                </label>

                <div className="flex flex-wrap items-center gap-x-6 gap-y-3 sm:col-span-2">
                  <button type="submit" className="h-12 min-w-[15rem] rounded-[2px] bg-brand px-8 text-[0.875rem] font-semibold uppercase tracking-[0.094rem] text-charcoal transition-colors duration-300 hover:bg-brand-dark hover:text-white">Envoyer</button>
                  <p className="max-w-[16rem] text-xs leading-relaxed text-ink">Votre messagerie s'ouvrira avec votre demande déjà rédigée.</p>
                </div>
              </form>
            )}
          </div>

          {/* Colonne latérale */}
          <aside className="space-y-12 lg:border-l lg:border-line lg:pl-14">
            {side.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <h3 className="font-sans text-[1.5rem] font-bold normal-case leading-tight tracking-normal text-black">{s.title}</h3>
                <p className="mt-3 text-[1rem] leading-relaxed text-ink">{s.text}</p>
                <a href={s.cta.href} {...(s.cta.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})} className="group mt-5 inline-flex items-center gap-3 py-1 text-[0.875rem] font-semibold uppercase tracking-[0.094rem] text-charcoal transition-colors hover:text-brand-darker">
                  {s.cta.label}<span aria-hidden className="h-px w-9 bg-brand transition-all duration-300 group-hover:w-14" />
                </a>
              </Reveal>
            ))}
            <Reveal delay={0.24}>
              <h3 className="font-sans text-[1.5rem] font-bold normal-case leading-tight tracking-normal text-black">Horaires</h3>
              <dl className="mt-3 space-y-1.5 text-[0.9375rem]">
                {siteInfo.hours.map((h) => <div key={h.day} className="flex justify-between gap-6 border-b border-line py-1.5"><dt className="text-ink">{h.day}</dt><dd className="font-medium">{h.time}</dd></div>)}
              </dl>
            </Reveal>
            <Reveal delay={0.3}><div><CtaLink to="/portfolio">Voir nos réalisations</CtaLink></div></Reveal>
          </aside>
        </div>
      </section>
    </div>
  );
}
